import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { ConceptoAporte } from '../types/afiliado';

/**
 * Hooks para administracion de conceptos de aporte.
 * Acceso restringido a admin_general (controlado por RLS + UI).
 *
 * Los conceptos NO se borran fisicamente porque pueden estar referenciados
 * en planilla_detalle y afiliado_aportes_extra. Solo se activan/desactivan
 * via el flag `activo`.
 */

const QK = {
  all: ['conceptos_aporte_admin'] as const,
  list: (incluirInactivos: boolean) =>
    ['conceptos_aporte_admin', 'list', { incluirInactivos }] as const,
};

export type ConceptoAportePayload = Omit<ConceptoAporte, 'id'>;
export type ConceptoAporteUpdatePayload = Partial<ConceptoAportePayload>;

// ----- LIST (todos, no solo opcionales) -----

export function useConceptosAporteAll(incluirInactivos = true) {
  return useQuery({
    queryKey: QK.list(incluirInactivos),
    queryFn: async () => {
      let query = supabase.from('conceptos_aporte').select('*').order('codigo');
      if (!incluirInactivos) query = query.eq('activo', true);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ConceptoAporte[];
    },
  });
}

// ----- CREATE -----

async function createConcepto(payload: ConceptoAportePayload): Promise<ConceptoAporte> {
  // Normalizamos codigo a mayusculas y sin espacios extra
  const normalized = {
    ...payload,
    codigo: payload.codigo.trim().toUpperCase(),
    descripcion: payload.descripcion.trim(),
  };
  const { data, error } = await supabase
    .from('conceptos_aporte')
    .insert(normalized)
    .select('*')
    .single();
  if (error) throw error;
  return data as ConceptoAporte;
}

export function useCreateConceptoAporte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createConcepto,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
      // Tambien invalidamos la otra query (la usada por el tab de aportes extra)
      qc.invalidateQueries({ queryKey: ['conceptos_aporte'] });
    },
  });
}

// ----- UPDATE -----

async function updateConcepto({
  id,
  payload,
}: {
  id: string;
  payload: ConceptoAporteUpdatePayload;
}): Promise<ConceptoAporte> {
  const normalized = { ...payload };
  if (normalized.codigo) normalized.codigo = normalized.codigo.trim().toUpperCase();
  if (normalized.descripcion) normalized.descripcion = normalized.descripcion.trim();

  const { data, error } = await supabase
    .from('conceptos_aporte')
    .update(normalized)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ConceptoAporte;
}

export function useUpdateConceptoAporte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateConcepto,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.invalidateQueries({ queryKey: ['conceptos_aporte'] });
    },
  });
}

// ----- TOGGLE ACTIVO -----

export function useToggleConceptoAporte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, activo }: { id: string; activo: boolean }) => {
      const { data, error } = await supabase
        .from('conceptos_aporte')
        .update({ activo })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as ConceptoAporte;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
      qc.invalidateQueries({ queryKey: ['conceptos_aporte'] });
    },
  });
}
